<script setup lang="ts">
import AdyenCheckout from "@adyen/adyen-web";
import "@adyen/adyen-web/dist/adyen.css";
import type DropinElement from "@adyen/adyen-web/dist/types/components/Dropin/Dropin";
import type { OnPaymentCompletedData } from "@adyen/adyen-web/dist/types/components/types";
import type { PaymentAction } from "@adyen/adyen-web/dist/types/types";

import { usePostTokenisationPaymentMutation } from "~/queries/finance/tokens";
import type { PaymentCompleted, PaymentSession } from "~/types/Payment";

type Props = {
  session?: PaymentSession;
  redirectUrl?: string;
};
const { session, redirectUrl } = defineProps<Props>();

const emit = defineEmits<{
  (e: "payment-failed", payload?: Error): void;
  (
    e: "payment-completed",
    payload: OnPaymentCompletedData | PaymentCompleted,
  ): void;
}>();

const emitPaymentCompleted = (
  result: OnPaymentCompletedData | PaymentCompleted,
) => {
  emit("payment-completed", result);
};

let dropin: DropinElement | null = null;
const isDropinMounted = ref(false);
const config = useRuntimeConfig();
const localePath = useLocalePath();
const { locale } = useI18n();
const { mutateAsync: submitTokenisationPaymentMutation } =
  usePostTokenisationPaymentMutation();

const onPaymentFailed = (error?: Error, component?: any) => {
  emit("payment-failed", error);

  if (error)
    // eslint-disable-next-line no-console
    console.error(
      "Adyen Dropin Error",
      error.name,
      error.message,
      error.stack,
      component,
    );
};

const returnUrl = computed(() => {
  const baseReturnUrl = `${window.location.origin}/${locale.value}/my-account/payment/redirect?sessionId=${session?.id}`;

  return redirectUrl
    ? `${baseReturnUrl}&redirect=${redirectUrl}`
    : baseReturnUrl;
});

const onSubmitTokenisationPayment = async (state: { data: PaymentRequest }) => {
  if (!session) return;

  const request = {
    paymentRequest: JSON.stringify({
      ...state.data,
    }),
    adyenPaymentId: session.id,
    returnUrl: returnUrl.value,
  };
  try {
    const response = await submitTokenisationPaymentMutation(request);

    if (response.refusalReasonCode) {
      onPaymentFailed();

      return await navigateTo(
        localePath({
          path: "/my-account/payment/add-new-card",
          query: {
            error: response.refusalReasonCode,
          },
        }),
      );
    }

    if (response.action === "null" && !!response.resultCode)
      return emitPaymentCompleted({
        resultCode: response.resultCode,
        refusalReasonCode: response.refusalReasonCode,
      });

    if (!!response.action && dropin) {
      dropin.handleAction(JSON.parse(response.action) as PaymentAction);
    }
  } catch (e) {
    onPaymentFailed(e as Error);
  }
};

const setupPaymentDropin = async () => {
  if (!session) return;

  const checkout = await AdyenCheckout({
    session: session,
    environment: config.public.adyen.environment,
    clientKey: config.public.adyen.clientKey,
    onPaymentCompleted: emitPaymentCompleted,
    onError: onPaymentFailed,
    paymentMethodsConfiguration: {
      card: {
        hasHolderName: true,
        holderNameRequired: true,
        billingAddressRequired: false,
      },
    },
    onSubmit: onSubmitTokenisationPayment,
  });
  dropin = checkout.create("dropin", {
    showStoredPaymentMethods: false,
  });
  dropin.mount("#dropin-container");
  isDropinMounted.value = true;
};

const onReloadDropin = async () => {
  // Remove the dropin if it's already loaded.
  // Means we're remounting the dropin for new payment methods.
  if (dropin) {
    dropin.remove();
    isDropinMounted.value = false;
  }
  try {
    await setupPaymentDropin();
  } catch {
    // something went wrong, reload the app (`reloadNuxtApp` has built-in reload-loop protection)
    reloadNuxtApp();
  }
};
watch(
  () => session,
  (newSession) => {
    if (!newSession) return;
    onReloadDropin();
  },
  { immediate: true },
);
</script>

<template>
  <div class="adyen-payment-dropin__wrapper">
    <BaseLoadingSpinner v-if="!session || !isDropinMounted" />

    <div
      id="dropin-container"
      class="order-payment__adyen-dropin"
    />
  </div>
</template>

<style lang="postcss" scoped>
.order-payment__adyen-dropin {
  :deep(.adyen-checkout__payment-methods-list) {
    .adyen-checkout__button.adyen-checkout__button--pay {
      /* overriding the background: transparent tailwind preflight styling */
      background-color: #00112c;
    }
    .adyen-checkout__payment-method {
      border-radius: 0 !important;
      margin-bottom: 1rem !important;
      margin-top: 1rem !important;
      .adyen-checkout__payment-method__header {
        padding-top: 0.5rem !important;
        padding-bottom: 0.5rem !important;
      }
    }
    .adyen-checkout__payment-method--selected
      > .adyen-checkout__payment-method__header {
      margin-bottom: 1rem !important;
    }
    button.adyen-checkout__button {
      border-radius: 0 !important;
    }
    .adyen-checkout__dropdown__button {
      border-radius: 0 !important;
    }
    input {
      border-radius: 0 !important;
    }
    .adyen-checkout__input {
      border-radius: 0 !important;
    }
    /*
    * Sometimes we see mismatches in the amount displayed in the dropin and the order details
    * Just hide the amount from here to prevent this.
    */
    :deep(.adyen-checkout__qr-loader--bcmc_mobile) {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .adyen-checkout__qr-loader__payment_amount {
        display: none;
      }
    }
  }
}
</style>
